import {
	parsePhoneNumber,
	parsePhoneNumberFromString,
	type CountryCode
} from 'libphonenumber-js';

import {
	getCallingCodeForIso,
	getCountriesForCallingCode,
	getDefaultCountryIso,
	getExtensionPrefixForIso,
	getMainCountryForCallingCode,
	splitCallingCodePrefix
} from '@/utils/formatters/phoneCountryCode';

export type PhoneNumberParts = {
	countryIso: string;
	nationalNumber: string;
};

const NANP_TOLL_FREE_AREA_CODES = new Set([
	'800',
	'833',
	'844',
	'855',
	'866',
	'877',
	'888'
]);

// "#" has no letters but is Taiwan's native extension marker (e.g. "912 345
// 678#10") - without it here, typing "#" before the base number is complete
// would fall through to sanitizeDigits and get silently stripped, merging the
// extension digits into the phone number.
export const isFreeTextPhoneValue = (value: string): boolean =>
	/[a-z#]/i.test(value);

export const sanitizeDigits = (value: string): string =>
	value.replace(/\D/g, '');

export const normalizeIddPrefix = (phone: string): string => {
	const match = phone.match(/^00(\d.*)$/);
	return match ? `+${match[1]}` : phone;
};

export const isCompletePhoneNumber = (
	countryIso: string,
	nationalNumber: string
): boolean => {
	if (!countryIso || !nationalNumber) return false;

	try {
		return !!parsePhoneNumber(
			nationalNumber,
			countryIso as CountryCode
		)?.isValid();
	} catch {
		return false;
	}
};

const resolveCountryForCallingCode = (
	callingCode: string,
	defaultCountryName?: string | null
): string => {
	const candidates = getCountriesForCallingCode(callingCode);
	const defaultIso = getDefaultCountryIso(defaultCountryName);

	if (candidates.some(o => o.iso === defaultIso)) return defaultIso;
	if (candidates.length === 1) return candidates[0].iso;

	return getMainCountryForCallingCode(callingCode)?.iso ?? defaultIso;
};

const sliceAfterCallingCode = (
	phone: string,
	callingCodeLength: number
): string => {
	let digitsSeen = 0;
	let i = 0;
	for (; i < phone.length; i++) {
		if (/\d/.test(phone[i])) {
			digitsSeen++;
			if (digitsSeen === callingCodeLength) {
				i++;
				break;
			}
		}
	}

	return phone.slice(i).replace(/^[\s,]+/, '');
};

export const splitPhoneNumber = (
	raw: string | null | undefined,
	defaultCountryName?: string | null
): PhoneNumberParts => {
	const rawPhone = (raw ?? '').trim();
	const phone = normalizeIddPrefix(rawPhone);

	if (phone.startsWith('+')) {
		const parsed = parsePhoneNumberFromString(phone);
		if (parsed) {
			const isAmbiguousNanpTollFree =
				getCallingCodeForIso(parsed.country) === '1' &&
				NANP_TOLL_FREE_AREA_CODES.has(parsed.nationalNumber.slice(0, 3));

			const countryIso = isAmbiguousNanpTollFree
				? 'US'
				: (parsed.country ??
					resolveCountryForCallingCode(
						parsed.countryCallingCode,
						defaultCountryName
					));

			return {
				countryIso,
				nationalNumber: sliceAfterCallingCode(
					phone,
					parsed.countryCallingCode.length
				)
			};
		}

		const prefixMatch = splitCallingCodePrefix(sanitizeDigits(phone));
		if (prefixMatch) {
			return {
				countryIso: resolveCountryForCallingCode(
					prefixMatch.callingCode,
					defaultCountryName
				),
				nationalNumber: sliceAfterCallingCode(
					phone,
					prefixMatch.callingCode.length
				)
			};
		}
	}

	return {
		countryIso: getDefaultCountryIso(defaultCountryName),
		nationalNumber: rawPhone
	};
};

export const refineAmbiguousCountryIso = (
	countryIso: string,
	nationalNumber: string
): string => {
	const callingCode = getCallingCodeForIso(countryIso);
	if (!callingCode || getCountriesForCallingCode(callingCode).length < 2) {
		return countryIso;
	}

	try {
		const parsed = parsePhoneNumberFromString(
			`+${callingCode}${nationalNumber}`
		);

		return parsed?.country ?? countryIso;
	} catch {
		return countryIso;
	}
};

// Matches a marker word/symbol ("ext", "ext.", "x", "#", "anexo", "int", "int.").
// Bare "ext"/"anexo"/"int" need a lookahead so they don't match inside words
// like "extra"/"international"; with a period they're already unambiguous, and
// adding the lookahead there would let the engine backtrack off the optional
// period (e.g. "ext" out of "ext.d"), stranding a "." that gets double-appended.
// "x"/"#" need no lookahead - only ever matched at the first non-digit char, so
// never mid-word. No separator required before the marker: standardized
// submission values glue it straight onto the digits (e.g. "7036267067xhello"),
// which also breaks `findPhoneNumbersInText`, so we split before parsing.
const EXTENSION_MARKER_AT_START =
	/^(?:ext\.:?|ext(?![a-z])|anexo(?![a-z])|int\.|int(?![a-z])|x|#)/i;

const MARKER_WORD_PREFIXES = ['ext', 'anexo', 'int'];

const isMarkerWordInProgress = (candidate: string): boolean => {
	const lower = candidate.toLowerCase();
	return MARKER_WORD_PREFIXES.some(prefix => prefix.startsWith(lower));
};

type NumberSplit = {
	leadingRaw: string;
	leadingDigits: string;
	suffix: string;
};

const splitAtFirstMarkerChar = (value: string): NumberSplit => {
	const idx = value.search(/[a-z#]/i);
	if (idx === -1) {
		return {
			leadingRaw: value,
			leadingDigits: sanitizeDigits(value),
			suffix: ''
		};
	}

	return {
		leadingRaw: value.slice(0, idx).replace(/[\s,]+$/, ''),
		leadingDigits: sanitizeDigits(value.slice(0, idx)),
		suffix: value.slice(idx)
	};
};

const appendMarker = (marker: string, afterMarker: string): string => {
	if (!afterMarker) return ` ${marker}`;

	const rest = afterMarker.replace(/^[\s,]+/, '');
	if (!rest) return ` ${marker} `;

	const isCompactMarker = marker.toLowerCase() === 'x' || marker === '#';
	return isCompactMarker && /^\d/.test(rest)
		? ` ${marker}${rest}`
		: ` ${marker} ${rest}`;
};

const appendMarkerSuffix = (countryIso: string, suffix: string): string => {
	if (!suffix) return suffix;

	const markerMatch = suffix.match(EXTENSION_MARKER_AT_START);
	if (markerMatch) {
		const marker = getExtensionPrefixForIso(countryIso).trimEnd();
		return appendMarker(marker, suffix.slice(markerMatch[0].length));
	}

	return suffix;
};

export const formatNationalNumber = (
	countryIso: string,
	nationalNumber: string
): string => {
	if (!countryIso || !nationalNumber) return nationalNumber;

	const { leadingRaw, leadingDigits, suffix } =
		splitAtFirstMarkerChar(nationalNumber);

	let formattedLeading = leadingRaw;
	try {
		const parsed = leadingDigits
			? parsePhoneNumber(leadingDigits, countryIso as CountryCode)
			: undefined;
		if (parsed?.isValid()) {
			formattedLeading = parsed.formatInternational().replace(/^\+\d+\s*/, '');
		}
	} catch {
		// Not a valid number for this country - leave the leading part exactly
		// as typed rather than guessing at a reformat.
	}

	const appended = appendMarkerSuffix(countryIso, suffix);
	if (!appended) return formattedLeading;

	const needsSeparator = !!formattedLeading && !appended.startsWith(' ');
	return formattedLeading + (needsSeparator ? ' ' : '') + appended;
};

export const combinePhoneNumber = ({
	countryIso,
	nationalNumber
}: PhoneNumberParts): string => {
	const raw = (nationalNumber ?? '').trim();
	if (!raw) return '';

	const callingCode = getCallingCodeForIso(countryIso);
	return callingCode ? `+${callingCode} ${raw}` : raw;
};

const standardizeNationalNumber = (nationalNumber: string): string | null => {
	if (!nationalNumber) return nationalNumber;

	const { leadingDigits, suffix } = splitAtFirstMarkerChar(nationalNumber);
	if (!suffix) return leadingDigits;

	const resolved = suffix.match(EXTENSION_MARKER_AT_START);
	if (resolved) {
		const token = resolved[0];
		const rest = suffix.slice(token.length);
		const trimmedRest = rest.replace(/^[\s,]+/, '');
		const extensionText = /^\d/.test(trimmedRest) ? trimmedRest : rest;
		return `${leadingDigits}x${extensionText}`;
	}

	return isMarkerWordInProgress(suffix) ? `${leadingDigits}${suffix}` : null;
};

export const standardizePhoneNumberForSubmission = (
	phoneNumber: string | null | undefined
): string => {
	const trimmed = (phoneNumber ?? '').trim();
	if (!trimmed) return '';

	const { countryIso, nationalNumber } = splitPhoneNumber(trimmed);
	const standardizedNational = standardizeNationalNumber(nationalNumber);
	if (standardizedNational === null) return trimmed;

	// US numbers are submitted with no "+1"
	if (countryIso === 'US') return standardizedNational;

	const callingCode = getCallingCodeForIso(countryIso);
	return callingCode
		? `+${callingCode}${standardizedNational}`
		: standardizedNational;
};
